import { registerBrick, Query, CloudObject, ErrorFlow } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
import React from 'react';
import { executeLambda } from '../../node/BackendData/LambdaExecutor.js';
import { FiltersAutocomplete } from '../shared/FiltersAutocomplete.jsx';
import DropdownConfig from '../shared/filters/DropdownConfig.js';
import { BCP_WORKFLOW_BUSINESS_NAMES } from '../../common/section_bcp/BcpWorkflowAuthorizations.js';

export default class CodedBCPFiltersAutocomplete extends ReactBrick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Array<*>} properties
     * @return {Element}
     */
    static getReactComponent($) {
        return (props) => {
            const entity = useProperty($, 'Entity');
            const fetchBcp21 = useProperty($, 'BCP21');
            const selectedFilters = useProperty($, 'Selected Filters') || {};
            const property = useProperty($, 'property') || {};
            // Add limitcheck property to set a input text value 
            const limitCheck = useProperty($, 'Limit Check');

            /** @type {DropdownConfig}*/
            const dropdownOptionsType = useProperty($, 'Dropdown Config');
            let dropdownOptions = [];

            // get the selected values for that filter entity
            const selectionForEntity = selectedFilters[property] || {};
            const selectedOptions = selectionForEntity.values || [];
            const showSelectAll = selectionForEntity.selectedAll || false;

            const componentId = `BCPFiltersAutocomplete_${property}`;

            const updateHeight = () => {
                const element = document.getElementById(componentId);
                if (element && element.parentElement) {
                    $.set('Height', Math.max(element.parentElement.clientHeight, 70));
                }
            };
            let fetchOptions;
            if (dropdownOptions?.length > 0) {
                fetchOptions = () => {
                    return Promise.resolve(dropdownOptions);
                };
            } else {
                fetchOptions = async (searchValue) => {
                    const customErrorCode = 2311052908;

                    const resultsString = await executeLambda($, fetchBcp21, [searchValue], customErrorCode);
                    if (resultsString instanceof ErrorFlow) {
                        const msg = resultsString.getMessage();
                        console.error(`Manage BCP filters error: ${msg}`);
                        return;
                    }
                    let results = JSON.parse(resultsString).results.map(result => {
                        const keys = Object.keys(result);
                        //Show the business names for workflow state values
                        if (keys.includes("workflowState")) {
                            return {
                                id: result[keys[0]] ? BCP_WORKFLOW_BUSINESS_NAMES[result[keys[0]]] : null,
                                value: result[keys[0]] ? result[keys[0]] : null,
                                description: result[keys[1]] ? result[keys[1]] : null
                            };
                        }
                        else if (keys.includes("businessPartner") || keys.includes("procurementManager")
                            || keys.includes("requestor") || keys.includes("technicalApplicationGroup")
                            || keys.includes("qualityAssurance") || keys.includes("qaInFactory")) {
                            //values of above all fields are expected to be in <username;email> format
                            const userDetails = result[keys[0]] && result[keys[0]].split(';');
                            return {
                                id: result[keys[0]] ? `${userDetails[0]} ${userDetails[1]}` : null,
                                value: result[keys[0]] ? result[keys[0]] : null,
                                description: result[keys[1]] ? result[keys[1]] : null
                            };
                        }
                        else if (keys.includes("spendCategory1")) {
                            return {
                                id: result[keys[0]] ? result[keys[0]] : null,
                                value: result[keys[0]] ? result[keys[0]] : null
                            };
                        } else if (keys.includes("requestDate")) {
                            const options = { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "numeric" };
                            return {
                                id: result[keys[0]] ? new Date(result[keys[0]]).toLocaleDateString("en-US", options) : null,
                                value: result[keys[0]] ? new Date(result[keys[0]]) : null,
                                description: result[keys[1]] ? result[keys[1]] : null
                            };
                        }
                        else {
                            return {
                                id: result[keys[0]] ? result[keys[0]] : null,
                                value: result[keys[0]] ? result[keys[0]] : null,
                                description: result[keys[1]] ? result[keys[1]] : null
                            };
                        }

                    });

                    results = results.filter(item => item.id !== null)
                    return results.length > 0 ? results : [];
                };
            }

            const onChangeHandler = (newSelection, selectedAll) => {
                const updatedFilters = {
                    ...selectedFilters,
                    [property]: {
                        values: newSelection,
                        selectedAll: selectedAll,
                    },
                };


                const onChangeBrick = $.getParent().get('On Change');
                const [triggerInput, updatedFiltersInput] = onChangeBrick.getInputs();
                const [endOutput] = onChangeBrick.getOutputs();
                const onChangeBrick$ = $.runner(onChangeBrick)
                    .set(updatedFiltersInput, updatedFilters)
                    .trigger(triggerInput);
                onChangeBrick$.waitFor(endOutput).finally(() => { onChangeBrick$.destroy(); });
            };

            return <FiltersAutocomplete
                id={componentId}
                disabled={false}
                fetchOptions={fetchOptions}
                getOptionLabel={CodedBCPFiltersAutocomplete.getOptionLabel}
                selectedOptions={selectedOptions}
                showSelectAll={showSelectAll}
                onChange={onChangeHandler}
                updateHeight={updateHeight}
                limitCheck={limitCheck}
            />;

        };
    }
}

/**
 * @param {FiltersAutocompleteOption} option
 * @return {string}
 */
CodedBCPFiltersAutocomplete.getOptionLabel = (option) => {
    if (typeof option.id === 'string' && option.id !== '') {
        return (parseInt(option.id) && option.id.length > 4 && option.description) ? // made for removing leading 0000 (I suppose). Testing for length > 4 seems to exclude plant ids
            `${parseInt(option.id)} - ${option.description}` :
            (parseInt(option.id) && option.id.length > 4 && option.description === null) ? `${parseInt(option.id)}` :
                `${option.id}` + ((option.description && option.description !== 'null') ? `- ${option.description}` : '');
    } else return `${option.description}`;
};


registerBrick('018c3872378ec122569f', CodedBCPFiltersAutocomplete);


